/* eslint-disable react-refresh/only-export-components */
// /home/project/src/contexts/AuthContext.tsx
import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { CanonicalAccountRole, User, UserRole } from '../types';
import type { MvpAccountRole } from '../types';
import { useSupabaseAuth, MfaStatus, MfaState } from '../hooks/useSupabaseAuth';
import { useSupabaseUsers } from '../hooks/useSupabaseUsers';
import { logSecurityEvent } from '../utils/securityUtils';
import { setUser as setMonitoringUser, clearUser as clearMonitoringUser } from '../services/MonitoringService';
import { IS_DEMO_MODE, LOCAL_BOOTSTRAP_ROLE } from '../config/env';
import { setDemoAuthorityActor } from '@/demo/demoAuthoritySession';
import { setDemoCurrentUser as setDemoProjectRoleActor } from '@/services/ProjectRoleChangeService';
import { normalizeAccountRole } from '../lib/auth/accountRoles';
import { createShowcaseAccounts, scopedRelationshipPersonas } from '../demo/showcaseCatalog';
import {
  DEMO_ACCOUNT_ROLE_PERSONA,
  normalizeDemoPersonaKey,
  type DemoPersonaKey,
} from '../lib/auth/demoRoleNavigation';
import { clearWorkspaceContextHint } from '@/lib/auth/contextSwitching';
import { clearAllSessions } from '@/lib/navigation/navigationMemoryStore';
import { runWorkspaceContextReset } from '@/lib/workspace/workspaceContextReset';
import { resolveRouteAuthority, resolveSafeLandingDestination } from '@/lib/auth/routeAuthority';

// Extended login result for AuthContext
export interface AuthLoginResult {
  ok: boolean;
  mfaRequired?: boolean;
  mfaEnrollRequired?: boolean;
  error?: string;
}

/**
 * Demo-only scoped-relationship personas. These are resource-scoped facts
 * (mounted from the scoped-persona catalog), NOT account roles, and are only
 * selectable while inspecting the local demo.
 */
export type ScopedPersonaKey =
  | 'workspace_member'
  | 'company_representative'
  | 'project_manager'
  | 'project_contributor'
  | 'partner_representative'
  | 'vendor_representative'
  | 'investor_representative'
  | 'investment_manager'
  | 'reporting_recipient'
  | 'decision_participant'
  | 'validator'
  | 'observer';

export interface DemoSessionTransition {
  readonly user: User | null;
  readonly authorityVersion: number;
  readonly landing: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<AuthLoginResult>;
  logout: () => void;
  /**
   * @deprecated Use capability predicates from lib/auth/capabilities.ts.
   */
  hasPermission: (permission: string) => boolean;
  switchRole: (role: UserRole | ScopedPersonaKey | MvpAccountRole) => DemoSessionTransition | null; // demo-only
  resetDemoSession: () => DemoSessionTransition | null;
  signUp: (email: string, password: string, userData: Record<string, unknown>) => Promise<{ error?: string }>;
  loading: boolean;
  isAuthenticated: boolean;
  isDemoMode: boolean;
  authorityVersion: number;
  // MFA properties
  mfaStatus: MfaStatus;
  mfaState: MfaState;
  verifyMfa: (code: string) => Promise<{ success: boolean; error?: string }>;
  verifyWithRecoveryCode: (code: string) => Promise<{ success: boolean; error?: string }>;
  cancelMfa: () => void;
  pendingMfaEmail: string | null;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ---- Canonical showcase accounts --------------------------------------------
const demoUsers = createShowcaseAccounts();

// ---- Permissions (updated to match new UserRole enum) ----------------------
const rolePermissions: Record<CanonicalAccountRole, string[]> = {
  platform_owner: [
    'platform.control.read', 'platform.approvals.manage', 'users.platform.read',
    'reports.read', 'reports.export', 'security.read', 'audit.read'
  ],
  platform_admin: [
    'platform.control.read', 'platform.operations.manage', 'users.platform.manage',
    'security.manage', 'audit.read'
  ],
  security_admin: [
    'platform.control.read', 'security.manage', 'access.approve', 'audit.export'
  ],
  support_admin: [
    'platform.control.read', 'support.manage', 'access.request', 'audit.read'
  ],
  commercial_admin: [
    'platform.control.read', 'customers.manage', 'commercial.manage', 'audit.read'
  ],
  implementation_admin: [
    'platform.control.read', 'customers.onboard', 'access.request', 'audit.read'
  ],
  organization_admin: [
    'workspace.users.manage', 'workspace.structure.manage', 'workspace.settings.manage',
    'reports.read', 'reports.export'
  ],
  customer_security_admin: [
    'workspace.security.manage', 'workspace.audit.export', 'workspace.access.manage'
  ],
  business_admin: [
    'projects.create', 'projects.read', 'tasks.read', 'files.read', 'users.read', 'users.create',
    'reports.read', 'reports.create', 'reports.export', 'billing.manage'
  ],
  system_admin: [
    'platform.control.read', 'platform.operations.manage', 'platform.releases.manage',
    'access.request', 'security.read'
  ],
  workspace_admin: [
    'projects.read', 'projects.update', 'tasks.manage', 'files.manage',
    'workspace.participants.manage'
  ],
  // Scoped relationship personas are NOT account roles.
  // Their capabilities are managed separately via LEGACY_SCOPED_ROLE_CAPABILITIES in capabilities.ts
  // and resolved via scoped relationship lookup, not global role assignment
};

// ---- Demo persistence helpers ----------------------------------------------
const LOCAL_ROLE_KEY = 'ax1_local_session_role_v1';
const LOCAL_BOOTSTRAP_KEY = 'ax1_local_bootstrap_complete_v1';

/** AX1-only Demo session keys. A reset must never use localStorage.clear(). */
export const DEMO_SESSION_STORAGE_KEYS = [LOCAL_ROLE_KEY, LOCAL_BOOTSTRAP_KEY] as const;

const findDemoUserByEmail = (_email: string): User | undefined =>
  (Object.values(demoUsers) as User[]).find(u => u.email.toLowerCase() === _email.toLowerCase());

export const getDemoFallbackUser = (email: string): User | null => {
  if (!IS_DEMO_MODE) return null;
  return findDemoUserByEmail(email) ?? null;
};

export const getLocalBootstrapUser = (): User | null => {
  if (!IS_DEMO_MODE) return null;
  const normalizedRole = LOCAL_BOOTSTRAP_ROLE.trim().toLowerCase().replace(/[\s-]+/g, '_');
  const requestedRole = Object.prototype.hasOwnProperty.call(demoUsers, normalizedRole)
    ? normalizedRole as keyof typeof demoUsers
    : 'platform_owner';
  return demoUsers[requestedRole];
};

const getDemoUserForPersona = (personaKey: DemoPersonaKey): User | null => {
  const scopedPersona = scopedRelationshipPersonas[personaKey];
  if (scopedPersona) return scopedPersona;
  const catalogKey = DEMO_ACCOUNT_ROLE_PERSONA[personaKey as MvpAccountRole];
  if (!catalogKey) return null;
  return scopedRelationshipPersonas[catalogKey]
    ?? demoUsers[catalogKey as keyof typeof demoUsers]
    ?? null;
};

const getDemoUserByStoredRole = (): User | null => {
  const stored = localStorage.getItem(LOCAL_ROLE_KEY);
  if (!stored) return null;
  const personaKey = normalizeDemoPersonaKey(stored);
  if (!personaKey) {
    localStorage.removeItem(LOCAL_ROLE_KEY);
    return null;
  }
  if (stored !== personaKey) localStorage.setItem(LOCAL_ROLE_KEY, personaKey);
  return getDemoUserForPersona(personaKey);
};

const setDemoRole = (role: UserRole | null) => {
  if (role) localStorage.setItem(LOCAL_ROLE_KEY, role);
  else localStorage.removeItem(LOCAL_ROLE_KEY);
};

const hasCompletedLocalBootstrap = () => localStorage.getItem(LOCAL_BOOTSTRAP_KEY) === 'true';
const markLocalBootstrapComplete = () => localStorage.setItem(LOCAL_BOOTSTRAP_KEY, 'true');

// ----------------------------------------------------------------------------
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const {
    user: supabaseUser,
    loading: authLoading,
    signIn: supabaseSignIn,
    signUp: supabaseSignUp,
    signOut: supabaseSignOut,
    // MFA methods from hook
    mfaState,
    verifyMfa: supabaseVerifyMfa,
    verifyWithRecoveryCode: supabaseVerifyRecovery,
    resetMfaState
  } = useSupabaseAuth();

  const { users: dbUsers, updateLastActive, loading: dbUsersLoading } = useSupabaseUsers();

  const [user, setUser] = useState<User | null>(null);
  const [isDemoMode, setIsDemoMode] = useState(false);

  useEffect(() => {
    if (isDemoMode) setDemoProjectRoleActor(user?.id ?? '');
    setDemoAuthorityActor(isDemoMode ? user : null);
  }, [isDemoMode, user]);
  const [loading, setLoading] = useState(true);
  const [pendingMfaEmail, setPendingMfaEmail] = useState<string | null>(null);
  const authorityVersionRef = useRef(0);
  const [authorityVersion, setAuthorityVersion] = useState(0);

  const advanceAuthorityVersion = useCallback(() => {
    authorityVersionRef.current += 1;
    setAuthorityVersion(authorityVersionRef.current);
    return authorityVersionRef.current;
  }, []);

  const clearDemoAuthorityContext = useCallback((previousUser: User | null, nextUser: User | null) => {
    clearWorkspaceContextHint(previousUser?.id);
    clearWorkspaceContextHint();
    clearAllSessions();
    runWorkspaceContextReset({
      previousWorkspaceId: previousUser?.workspace_id ?? null,
      nextWorkspaceId: null,
      previousUserId: previousUser?.id ?? null,
      nextUserId: nextUser?.id ?? null,
    });
  }, []);

  // Initialize user state
  useEffect(() => {
    if (authLoading) return;
    // Wait for authoritative users (including scopedRelationships) before deciding
    // investor/workspace access — otherwise shells race an empty dbUsers list and
    // incorrectly redirect to /unauthorized.
    if (supabaseUser && dbUsersLoading) return;

    const initUser = async () => {
      try {
        if (supabaseUser) {
          // Real authenticated user
          const dbUser = dbUsers.find(u => u.id === supabaseUser.id);
          if (dbUser) {
            // AX1: Check if user is suspended and redirect to suspended page
            if (dbUser.status === 'suspended') {
              console.log('AuthContext: User is suspended, redirecting to /suspended');
              logSecurityEvent(
                'login',
                dbUser.id,
                { method: 'password', email: dbUser.email, blocked: true, reason: 'suspended' },
                'failure'
              );
              // Clear user state and redirect
              setUser(null);
              setIsDemoMode(false);
              setDemoRole(null);
              setLoading(false);
              window.location.href = '/suspended';
              return;
            }

            setUser(dbUser);
            setIsDemoMode(false);
            setDemoRole(null); // clear any demo persistence
            updateLastActive(supabaseUser.id);

            // Set user in monitoring service
            setMonitoringUser({
              id: dbUser.id,
              role: dbUser.role
            });

            logSecurityEvent(
              'login',
              dbUser.id,
              { method: 'password', email: dbUser.email },
              'success'
            );
          } else {
            console.warn('User exists in auth but not in users table:', supabaseUser.id);
            setUser(null);
            setIsDemoMode(false);
          }
        } else {
          // Not authenticated with Supabase

          // Guard: Only load demo users when explicitly in demo mode
          if (!IS_DEMO_MODE) {
            setUser(null);
            setIsDemoMode(false);
            setLoading(false);
            return;
          }

          // Local mode: restore an active session or seed the first Platform Owner session.
          const persistedDemo = getDemoUserByStoredRole();

          if (persistedDemo) {
            setUser(persistedDemo);
            setIsDemoMode(true);
            advanceAuthorityVersion();

            // Set demo user in monitoring service
            setMonitoringUser({
              id: persistedDemo.id,
              role: persistedDemo.role
            });

            // Do NOT re-persist here: the stored key (account role OR scoped
            // persona key) is already what produced persistedDemo. Re-saving
            // persistedDemo.role would clobber scoped-persona keys, whose
            // user.role is a generic 'member'.
          } else if (!hasCompletedLocalBootstrap()) {
            const bootstrapUser = getLocalBootstrapUser() ?? demoUsers.platform_owner;
            setUser(bootstrapUser);
            setIsDemoMode(true);
            setDemoRole(bootstrapUser.role);
            markLocalBootstrapComplete();
            advanceAuthorityVersion();

            // Set the browser-local bootstrap user in monitoring.
            setMonitoringUser({
              id: bootstrapUser.id,
              role: bootstrapUser.role
            });
          } else {
            // A prior local session was signed out. Stay signed out until the
            // user explicitly starts another local session from the login page.
            setUser(null);
            setIsDemoMode(true);
          }
        }
      } catch (err) {
        console.error('Error initializing user:', err);
        // Only bootstrap a local account in demo mode.
        if (IS_DEMO_MODE) {
          const bootstrapUser = getLocalBootstrapUser() ?? demoUsers.platform_owner;
          setUser(bootstrapUser);
          setIsDemoMode(true);
          setDemoRole(bootstrapUser.role);
          markLocalBootstrapComplete();
        } else {
          setUser(null);
          setIsDemoMode(false);
        }
      } finally {
        setLoading(false);
      }
    };

    // Initialize user
    initUser();
  }, [supabaseUser, authLoading, dbUsers, dbUsersLoading, updateLastActive, advanceAuthorityVersion]);

  const login = useCallback(async (email: string, password: string): Promise<AuthLoginResult> => {
    try {
      if (IS_DEMO_MODE) {
        const localUser = getDemoFallbackUser(email);
        if (!localUser) {
          return { ok: false, error: 'This local preview account is not available.' };
        }

        setUser(localUser);
        setIsDemoMode(true);
        setDemoRole(localUser.role);
        markLocalBootstrapComplete();
        advanceAuthorityVersion();
        setPendingMfaEmail(null);
        setMonitoringUser({ id: localUser.id, role: localUser.role });
        logSecurityEvent('login', localUser.id, { method: 'local_preview', email: localUser.email }, 'success');
        return { ok: true };
      }

      const result = await supabaseSignIn(email, password);

      if (!result.ok) {
        logSecurityEvent('login', 'unknown', { method: 'password', email, error: result.error }, 'failure');
        return { ok: false, error: result.error };
      }

      // Check MFA requirements
      if (result.mfaRequired) {
        setPendingMfaEmail(email);
        return { ok: true, mfaRequired: true };
      }

      if (result.mfaEnrollRequired) {
        setPendingMfaEmail(email);
        return { ok: true, mfaEnrollRequired: true };
      }

      // Success - no MFA needed, user state will be set by effect
      setPendingMfaEmail(null);
      return { ok: true };

    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Login failed' };
    }
  }, [supabaseSignIn, advanceAuthorityVersion]);

  const signUp = useCallback(async (email: string, password: string, userData: Record<string, unknown>) => {
    if (IS_DEMO_MODE) {
      return { error: 'Account creation requires the production authentication service.' };
    }

    try {
      const { error } = await supabaseSignUp(email, password, userData);
      if (error) throw new Error(error.message);

      logSecurityEvent('user_creation', 'system', { method: 'signup', email }, 'success');
      return { error: undefined };
    } catch (error) {
      logSecurityEvent(
        'user_creation',
        'system',
        { method: 'signup', email, error: error instanceof Error ? error.message : 'Unknown error' },
        'failure'
      );
      return { error: error instanceof Error ? error.message : 'Signup failed' };
    }
  }, [supabaseSignUp]);

  const logout = useCallback(async () => {
    if (user) {
      await logSecurityEvent('logout', user.id, { email: user.email }, 'success');
    }

    if (!isDemoMode) {
      await supabaseSignOut();
    }

    // Clear user state
    clearWorkspaceContextHint(user?.id);
    clearWorkspaceContextHint();
    clearAllSessions();
    runWorkspaceContextReset({
      previousWorkspaceId: user?.workspace_id ?? null,
      nextWorkspaceId: null,
      previousUserId: user?.id ?? null,
      nextUserId: null,
    });
    setUser(null);
    setIsDemoMode(false);
    setDemoRole(null);
    setPendingMfaEmail(null);

    // Reset MFA state
    resetMfaState();

    // Clear user from monitoring
    clearMonitoringUser();

    // Let other parts of the app (e.g., encryption) react if they listen for this
    window.dispatchEvent(new Event('ax1network-logout'));
  }, [user, isDemoMode, supabaseSignOut, resetMfaState]);

  /**
   * Verify MFA TOTP code
   */
  const verifyMfa = useCallback(async (code: string): Promise<{ success: boolean; error?: string }> => {
    const result = await supabaseVerifyMfa(code);

    if (result.success) {
      // MFA verified - clear pending email
      setPendingMfaEmail(null);
    }

    return result;
  }, [supabaseVerifyMfa]);

  /**
   * Verify using recovery code
   */
  const verifyWithRecoveryCode = useCallback(async (code: string): Promise<{ success: boolean; error?: string }> => {
    const result = await supabaseVerifyRecovery(code);

    if (result.success) {
      // Recovery code verified - clear pending email
      setPendingMfaEmail(null);
    }

    return result;
  }, [supabaseVerifyRecovery]);

  /**
   * Cancel MFA verification and return to login
   */
  const cancelMfa = useCallback(() => {
    setPendingMfaEmail(null);
    resetMfaState();
    // Sign out to clear partial auth state
    supabaseSignOut();
  }, [resetMfaState, supabaseSignOut]);

  /**
   * @deprecated Fail-closed. Legacy string RBAC must not authorize.
   * Use usePermissions().can(capability, context) or capability helpers.
   *
   * The forbidden fallback chain platform_role ?? workspace_role ?? role
   * is deliberately NOT used. Set AX1_ALLOW_LEGACY_RBAC=1 only for
   * emergency local diagnostics — never for production authority.
   */
  const hasPermission = useCallback((permission: string): boolean => {
    const allowLegacy =
      typeof process !== 'undefined' &&
      process.env &&
      process.env.AX1_ALLOW_LEGACY_RBAC === '1';

    const shouldWarn = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__ax1HasPermissionWarned ||= new Set<string>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const warned: Set<string> = (window as any).__ax1HasPermissionWarned;

    if (shouldWarn && !warned.has(permission)) {
      warned.add(permission);
      const stack = new Error().stack?.split('\n').slice(2, 5).map(l => l.trim()).join(' | ');
      console.warn(
        `[RBAC DENIED] hasPermission("${permission}") is fail-closed. ` +
        `MIGRATION REQUIRED: Use usePermissions().can(capability, context). Stack: ${stack}`,
      );
    }

    if (!allowLegacy || !user) return false;

    // Emergency diagnostic path only — still does not use the forbidden
    // platform_role ?? workspace_role ?? role chain.
    const normalizedRole = normalizeAccountRole(user.account_role ?? null);
    const userPermissions = (normalizedRole ? rolePermissions[normalizedRole as CanonicalAccountRole] : undefined) || [];
    if (userPermissions.includes(permission)) return true;
    const base = permission.split('.')[0];
    return userPermissions.some(p => p === `${base}.*` || p.startsWith(`${base}.`));
  }, [user]);

  const switchRole = useCallback((role: UserRole | ScopedPersonaKey | MvpAccountRole): DemoSessionTransition | null => {
    if (!isDemoMode) return null;
    const personaKey = normalizeDemoPersonaKey(role);
    const next = personaKey ? getDemoUserForPersona(personaKey) : null;
    if (!personaKey || !next) return null;

    if (user) {
      logSecurityEvent(
        'role_change',
        user.id,
        { oldRole: user.role, newRole: personaKey, method: 'demo_persona_switch' },
        'success',
      );
    }

    localStorage.setItem(LOCAL_ROLE_KEY, personaKey);
    markLocalBootstrapComplete();
    clearDemoAuthorityContext(user, next);
    // The new session's children may read Demo authority during their first render.
    // Do not leave the previous actor installed until the passive effect runs.
    setDemoProjectRoleActor(next.id);
    setDemoAuthorityActor(next);
    setUser(next);
    setMonitoringUser({ id: next.id, role: next.role });
    const nextAuthorityVersion = advanceAuthorityVersion();
    return {
      user: next,
      authorityVersion: nextAuthorityVersion,
      landing: resolveSafeLandingDestination(resolveRouteAuthority(next)).to,
    };
  }, [advanceAuthorityVersion, clearDemoAuthorityContext, isDemoMode, user]);

  const resetDemoSession = useCallback((): DemoSessionTransition | null => {
    if (!isDemoMode) return null;
    for (const key of DEMO_SESSION_STORAGE_KEYS) localStorage.removeItem(key);
    markLocalBootstrapComplete();
    clearDemoAuthorityContext(user, null);
    setDemoProjectRoleActor('');
    setDemoAuthorityActor(null);
    setUser(null);
    setPendingMfaEmail(null);
    clearMonitoringUser();
    return {
      user: null,
      authorityVersion: advanceAuthorityVersion(),
      landing: '/login',
    };
  }, [advanceAuthorityVersion, clearDemoAuthorityContext, isDemoMode, user]);

  // Only settled MFA states may render product routes. In particular,
  // `verifying` and `unavailable` must remain fail-closed.
  const isAuthenticated = !!user && (mfaState.status === 'none' || mfaState.status === 'verified');

  const value = useMemo<AuthContextType>(() => {
    return {
      user,
      loading,
      isAuthenticated,
      isDemoMode,
      authorityVersion,
      login,
      logout,
      hasPermission,
      switchRole,
      resetDemoSession,
      signUp,
      // MFA properties
      mfaStatus: mfaState.status,
      mfaState,
      verifyMfa,
      verifyWithRecoveryCode,
      cancelMfa,
      pendingMfaEmail
    };
  }, [
    user,
    loading,
    isDemoMode,
    authorityVersion,
    login,
    logout,
    hasPermission,
    isAuthenticated,
    switchRole,
    resetDemoSession,
    signUp,
    mfaState,
    verifyMfa,
    verifyWithRecoveryCode,
    cancelMfa,
    pendingMfaEmail
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
